import Dialog from "@mui/material/Dialog"
import React from "react"

const EMPTY_FUNC = () => {}
const DialogContext = React.createContext([EMPTY_FUNC, EMPTY_FUNC])
export const useDialog = () => React.useContext(DialogContext)

function DialogContainer(props) {
  const {
    children,
    open,
    onClose,
    onKill,
    maxWidth,
    disableBackdropClick = false,
    disableEscapeKeyDown = false,
  } = props

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === "backdropClick") {
      return
    }

    if (disableEscapeKeyDown && reason === "escapeKeyDown") {
      return
    }

    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={onKill}
      maxWidth={maxWidth || 700}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {children}
    </Dialog>
  )
}

export default function DialogProvider({ children }) {
  const [dialogs, setDialogs] = React.useState([])

  const createDialog = React.useCallback((option) => {
    const dialog = {
      ...option,
      id: `${Date.now()}-${Math.random()}`,
      open: true,
    }

    setDialogs((dialogs) => [...dialogs, dialog])
  }, [])

  const closeDialog = React.useCallback(() => {
    setDialogs((dialogs) => {
      const latestOpenDialogIndex = dialogs
        .map((dialog, index) => ({ dialog, index }))
        .reverse()
        .find(({ dialog }) => dialog.open)?.index

      if (latestOpenDialogIndex === undefined) {
        return dialogs
      }

      const latestDialog = dialogs[latestOpenDialogIndex]

      if (latestDialog.onClose) {
        latestDialog.onClose()
      }

      return dialogs.map((dialog, index) =>
        index === latestOpenDialogIndex ? { ...dialog, open: false } : dialog,
      )
    })
  }, [])

  const closeDialogById = React.useCallback((id) => {
    setDialogs((dialogs) => {
      const currentDialog = dialogs.find((dialog) => dialog.id === id)

      if (!currentDialog) {
        return dialogs
      }

      if (currentDialog.onClose) {
        currentDialog.onClose()
      }

      return dialogs.map((dialog) =>
        dialog.id === id ? { ...dialog, open: false } : dialog,
      )
    })
  }, [])

  const contextValue = React.useMemo(
    () => [createDialog, closeDialog],
    [createDialog, closeDialog],
  )

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      {dialogs.map((dialog) => {
        const { id, onClose, onExited, ...dialogParams } = dialog

        const handleKill = () => {
          if (onExited) {
            onExited()
          }

          setDialogs((dialogs) =>
            dialogs.filter((currentDialog) => currentDialog.id !== id),
          )
        }

        return (
          <DialogContainer
            key={id}
            onClose={() => closeDialogById(id)}
            onKill={handleKill}
            {...dialogParams}
          />
        )
      })}
    </DialogContext.Provider>
  )
}
